/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useStatsigClient, useStatsigUser } from '@statsig/react-bindings';
import {
  Checkbox,
  TextField,
  Banner,
  MaterialIcon,
  SpinningIcon,
  MTRFullLogo,
} from '@dsny/dsny-component-library';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useDispatch, useSelector } from 'react-redux';
import theme from 'src/styles/theme';
import { AppDispatch, RootState } from 'src/app/store';
import {
  AuthForm,
  getTokens,
  getSessionUser,
  getUploadService,
  identifyLoginPath,
  isCasterUser,
  signIn,
  signInFromToken,
} from 'src/features/Auth/Auth.thunks';
import { inputHandler } from 'src/utils/InputHandler';
import {
  getCompanyCountFromToken,
  getReleaseIdFromToken,
} from 'src/utils/Authenticate';
import { deleteEnterprisePlanFromLocalStorage } from 'src/utils/LocalStorageManager';
import {
  MULTI_COMPANY_ACCESS,
  TRACKING_PURCHASES_ENABLED,
} from 'src/types/FeataureFlags';
import { getPostAuthRedirectPath } from 'src/features/Auth/Auth.utils';
import {
  FormBox,
  CheckboxWrapper,
  Title,
  Container,
  ButtonWrapper,
  RememberMeSection,
  NotMemberSection,
  JoinCommunityLink,
  ForgotPasswordLink,
  LogoMTRWrapper,
  ButtonSection,
  ErrorWrapper,
} from './Signin.styles';
import { AuthWrapper } from '../Auth.styles';
import { clearSignupResponse } from '../Auth.slice';
import {
  identifyUser,
  trackEvent,
  initializeEventWrapper,
} from '../../../utils/segmentService';

type SigninFormValues = {
  email: string;
  password?: string;
};

const buildSchema = (requiresPassword: boolean) =>
  yup.object({
    email: yup.string().required(),
    password: requiresPassword
      ? yup.string().required()
      : yup.string().notRequired(),
  });

const EMAIL_FIELD: keyof SigninFormValues = 'email';
const PASSWORD_FIELD: keyof SigninFormValues = 'password';

const Signin: React.FC = () => {
  const { t } = useTranslation();
  const [rememberMe, setRememberMe] = useState<string>('');
  const [rerender, setRerender] = useState(false);
  const [tailingIconName, setTailingIconName] = useState('VisibilityOff');
  const [passwordType, setPasswordType] = useState('password');
  const [showPasswordStep, setShowPasswordStep] = useState(false);
  const [isCheckingLoginType, setIsCheckingLoginType] = useState(false);
  const [lastCheckedEmail, setLastCheckedEmail] = useState('');
  const [localSsoError, setLocalSsoError] = useState('');
  const { isFetching, isLoginSucessful, isUserEligibleForMtr, ssoLoginError } =
    useSelector((state: RootState) => state.auth);
  const { email } = useSelector(
    (state: RootState) => state.auth.signUp.stepOne
  );
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { client } = useStatsigClient();
  const { updateUserAsync } = useStatsigUser();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const logDebug = (...args: unknown[]) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[signin]', ...args); // TODO: remove this after QA testing
    }
  };
  const isTrackingReport = searchParams.get('istrackingreport') === 'true'; // Caster tracking report
  const openAirplayReport = searchParams.get('openairplayreport') === 'true';

  useEffect(() => {
    if (token) {
      dispatch(signInFromToken(token))
        .unwrap()
        .then(async () => {
          const user = getSessionUser();
          await updateUserAsync({
            userID: (user?.staffid ?? 'anonymous').toString(),
            email: user?.email,
          });
          identifyUser(user.staffid, {
            email: user.email,
            name: user.fullname,
            username: user.username,
            company_id: user.company_id,
          });
          const releaseId = getReleaseIdFromToken(token);
          trackEvent(
            'Login From Token',
            {
              email: user?.email,
              name: user?.fullname,
              username: user?.username,
              serviceType: getUploadService(),
              companyID: user?.company_id?.toString(),
              isCasterUser: 'true',
            },
            () => {
              if (isTrackingReport) {
                navigate(`/trackingreport?release=${releaseId}`);
                return;
              }

              if (openAirplayReport) {
                navigate(`/airplayreport`);
                return;
              }

              if (client.checkGate(TRACKING_PURCHASES_ENABLED)) {
                navigate(`/payment/multiasset?release=${releaseId}`);
                return;
              }

              navigate('/dashboard');
            }
          );
        })
        .catch(() => {
          // Do not navigate when 401 or 403
        });
    }
  }, [token]);

  useEffect(() => {
    if (email) {
      dispatch(clearSignupResponse());
    }
  }, [email]);

  useEffect(() => {
    // remove enterprise plan flag from session
    deleteEnterprisePlanFromLocalStorage();
  }, []);

  const schema = useMemo(
    () => buildSchema(showPasswordStep),
    [showPasswordStep]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm({
    mode: 'all',
    resolver: useMemo(() => yupResolver(schema), [schema]),
    shouldUnregister: true,
  });

  useEffect(() => {
    const rememberMeItem = localStorage.getItem('PLAYMPE_REMEMBERME');
    if (rememberMeItem) {
      setValue(EMAIL_FIELD, rememberMeItem);
      setRememberMe('checkValue');
    }
  }, [setValue]);

  // Setting remember me on local storage
  const addEmailToLocalStorage = (addValue: boolean) => {
    if (addValue && getValues(EMAIL_FIELD)) {
      localStorage.setItem('PLAYMPE_REMEMBERME', getValues(EMAIL_FIELD));
    } else {
      localStorage.removeItem('PLAYMPE_REMEMBERME');
    }
  };

  // Handling input field
  const handleInput = () => {
    setRerender(!rerender);
    if (!!rememberMe && getValues(EMAIL_FIELD)) {
      addEmailToLocalStorage(!!rememberMe);
    }
  };

  // Handling onSubmit button
  const onSubmit = (data: SigninFormValues) => {
    setRerender(!rerender);
    const payload: AuthForm = {
      ...data,
      identifier: uuid().replace(/-/gi, ''),
    };
    logDebug('submit_payload', { email: payload.email });
    if (rememberMe) {
      addEmailToLocalStorage(true);
    }
    if (isFetching) return;
    dispatch(signIn(payload))
      .unwrap()
      .then(async () => {
        const user = getSessionUser();
        await updateUserAsync({
          userID: (user?.staffid ?? 'anonymous').toString(),
          email: user?.email,
        });
        identifyUser(user.staffid, {
          email: user.email,
          name: user.fullname,
          username: user.username,
          company_id: user.company_id,
        });
        initializeEventWrapper(user.company_id);
        const companyCount = getCompanyCountFromToken(getTokens().token ?? '');
        const canAccessMultiCompany =
          companyCount > 1 && client.checkGate(MULTI_COMPANY_ACCESS);
        const redirectPath = canAccessMultiCompany
          ? getPostAuthRedirectPath('/multi-company', location.search)
          : getPostAuthRedirectPath('/home', location.search);
        trackEvent(
          'Login',
          {
            email: user?.email,
            name: user?.fullname,
            username: user?.username,
            serviceType: getUploadService(),
            companyID: user?.company_id?.toString(),
            isCasterUser: isCasterUser().toString(),
          },
          () => {
            navigate(redirectPath);
          }
        );
      })
      .catch(() => {
        // Do not navigate when 401 or 403
      });
  };

  // Handling checkbox
  const handleCheckbox = (e: React.SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    const checked = target.value;
    setRememberMe(checked ? '' : 'checkValue');
    addEmailToLocalStorage(!checked);
  };

  // Handling password visibility
  const handlePasswordVisibility = () => {
    if (tailingIconName === 'VisibilityOff') {
      setTailingIconName('Visibility');
      setPasswordType('text');
    } else {
      setTailingIconName('VisibilityOff');
      setPasswordType('password');
    }
  };

  // Handling continue button
  const handleContinue = async () => {
    logDebug('continue_start', { email: getValues(EMAIL_FIELD) });
    const isEmailValid = await trigger(EMAIL_FIELD);
    logDebug('continue_email_valid', { isEmailValid });
    if (!isEmailValid) return;

    setLocalSsoError('');
    setIsCheckingLoginType(true);
    try {
      const response = await identifyLoginPath(getValues(EMAIL_FIELD));
      logDebug('continue_login_type', response);
      if (response.login_type === 'password') {
        setShowPasswordStep(true);
        setLastCheckedEmail(getValues(EMAIL_FIELD));
      } else {
        if (response.redirect_url) {
          window.location.assign(response.redirect_url);
          return;
        }
        setLocalSsoError(t('SIGNIN_SSO_ERROR_DESC'));
      }
    } catch (error) {
      logDebug('continue_identify_failed', error);
      setShowPasswordStep(true);
      setLastCheckedEmail(getValues(EMAIL_FIELD));
    } finally {
      setIsCheckingLoginType(false);
    }
  };

  // Handling form submission
  const handleFormSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (!showPasswordStep) {
      await handleContinue();
      return;
    }

    logDebug('submit_attempt', {
      email: getValues(EMAIL_FIELD),
      hasPassword: !!getValues(PASSWORD_FIELD),
    });
    const isValid = await trigger([EMAIL_FIELD, PASSWORD_FIELD]);
    logDebug('submit_validation_result', { isValid });
    if (!isValid) {
      return;
    }
    handleSubmit(onSubmit as any, (formErrors) => {
      logDebug('submit_validation_failed', formErrors);
    })();
  };

  // Incorrect credentials error message
  const credentialErrorMessage = {
    title: t('SIGNIN_INCORRECT_CREDENTIALS'),
    description: t('SIGNIN_INVALID_FIELDS'),
  };

  // User not eligible for MTR error message
  const eligibilityErrorMessage = {
    title: t('SIGNIN_MULTIPLE_CASTER_COMPANIES_ERROR'),
    description: t('SIGNIN_MULTIPLE_CASTER_COMPANIES_ERROR_DESC'),
  };
  const ssoError = localSsoError || ssoLoginError;
  const ssoErrorMessage = {
    title: t('SIGNIN_SSO_ERROR_TITLE'),
    description: ssoError || t('SIGNIN_SSO_ERROR_DESC'),
  };
  const isActionLoading = showPasswordStep ? isFetching : isCheckingLoginType;

  return (
    <AuthWrapper>
      <LogoMTRWrapper>
        <MTRFullLogo />
      </LogoMTRWrapper>

      <Container>
        <FormBox onSubmit={handleFormSubmit}>
          {/* Incorrect credentials error message */}
          {showPasswordStep && !isLoginSucessful && (
            <ErrorWrapper>
              <Banner bannerContent={credentialErrorMessage} variant="error" />
            </ErrorWrapper>
          )}

          {/* SSO required error message */}
          {ssoError && (
            <ErrorWrapper>
              <Banner bannerContent={ssoErrorMessage} variant="error" />
            </ErrorWrapper>
          )}

          {/* User not eligible for MTR error message */}
          {showPasswordStep && !isUserEligibleForMtr && (
            <ErrorWrapper>
              <Banner bannerContent={eligibilityErrorMessage} variant="error" />
            </ErrorWrapper>
          )}

          <Title>{t('SIGNIN_TITLE')}</Title>
          <TextField
            id="email"
            data-cy="email"
            setting="mtr"
            labelText={t('SIGNIN_USERNAME')}
            type="text"
            required
            {...register(EMAIL_FIELD)}
            ref={null}
            error={errors?.email && t('SIGNIN_ERROR_USERNAME')}
            onBlur={(e: Event) => {
              inputHandler(setValue, trigger, e);
              handleInput();
            }}
            onChange={(e: ChangeEvent) => {
              const nextEmail = (e.target as HTMLInputElement).value;
              if (showPasswordStep && nextEmail !== lastCheckedEmail) {
                setShowPasswordStep(false);
                setValue(PASSWORD_FIELD, '');
                setTailingIconName('VisibilityOff');
                setPasswordType('password');
                setLocalSsoError('');
              }
              inputHandler(setValue, trigger, e);
              handleInput();
            }}
            value={getValues(EMAIL_FIELD) || ''}
            onFocus={() => setRerender(!rerender)}
            wrapperStyles={{
              paddingRight: '0px',
              paddingBottom: '32px',
              marginBottom: '0px',
            }}
            inputStyle={{
              height: '44px',
              borderRadius: '5px',
              borderColor: errors?.email && theme.colors.error50,
            }}
            labelStyle={{
              fontSize: '14px',
              color: errors?.email && theme.colors.error50,
            }}
            errorStyle={{ marginTop: '8px', color: theme.colors.error50 }}
          />
          {showPasswordStep && (
            <>
              <TextField
                id="password"
                data-cy="password"
                setting="mtr"
                labelText={t('SIGNIN_PASSWORD')}
                type={passwordType}
                required
                {...register(PASSWORD_FIELD)}
                ref={null}
                error={errors?.password && t('SIGNIN_ERROR_PASSWORD')}
                onBlur={(e: Event) => {
                  inputHandler(setValue, trigger, e);
                  handleInput();
                }}
                onChange={(e: ChangeEvent) => {
                  inputHandler(setValue, trigger, e);
                  handleInput();
                }}
                onClick={() => handlePasswordVisibility()}
                onFocus={() => setRerender(!rerender)}
                wrapperStyles={{
                  paddingRight: '0px',
                  paddingBottom: '0px',
                  marginBottom: '16px',
                }}
                inputStyle={{
                  height: '44px',
                  borderRadius: '5px',
                  borderColor: errors?.password && theme.colors.error50,
                }}
                labelStyle={{
                  fontSize: '14px',
                  color: errors?.password && theme.colors.error50,
                }}
                errorStyle={{ marginTop: '8px', color: theme.colors.error50 }}
                tailingIcon={tailingIconName}
                isTailingClickable
                tailingStyle={{
                  paddingTop: errors?.password ? '6px' : '20px',
                  paddingRight: '10px',
                }}
              />

              <RememberMeSection>
                <CheckboxWrapper>
                  <Checkbox
                    id="checkValue"
                    name="rememberMe"
                    data-cy="remember_me"
                    color={theme.colors.green}
                    onChange={handleCheckbox}
                    value={rememberMe}
                    checked={!!rememberMe}
                    style={{
                      borderColor: theme.colors.neutralW70,
                      marginRight: '8px',
                      marginBottom: '4px',
                    }}
                  />
                  <span>{t('SIGNIN_REMEMBER_ME')}</span>
                </CheckboxWrapper>

                <ForgotPasswordLink to="/auth/forgot_password">
                  {t('SIGNIN_FORGOT_PASSWORD_LINK')}
                </ForgotPasswordLink>
              </RememberMeSection>
            </>
          )}

          <ButtonSection>
            <ButtonWrapper
              setting="mtr"
              fontSize="14px"
              type="button"
              data-cy={showPasswordStep ? 'sign_in' : 'continue'}
              style={{
                fontFamily: theme.fonts.primary,
                height: '36px',
                borderRadius: '5px',
                paddingTop: isActionLoading ? '14px' : '',
              }}
              onClick={handleFormSubmit}
            >
              {isActionLoading ? (
                <SpinningIcon>
                  <MaterialIcon name="Rotate" />
                </SpinningIcon>
              ) : (
                t(
                  showPasswordStep
                    ? 'SIGNIN_CTA_BUTTON'
                    : 'SIGNIN_CONTINUE_BUTTON'
                )
              )}
            </ButtonWrapper>
          </ButtonSection>

          <NotMemberSection>
            {t('SIGNIN_NOT_A_MEMBER')}&nbsp;
            <JoinCommunityLink to="/auth/signup">
              {t('SIGNIN_JOIN_COMMUNITY_LINK')}
            </JoinCommunityLink>
          </NotMemberSection>
        </FormBox>
      </Container>
    </AuthWrapper>
  );
};

export default Signin;
